import React, { useMemo, useState } from "react";
import styles from "../EventDetails.module.scss";
import Text from "components/Text";
import { Col, Row } from "reactstrap";
import { withTranslation } from "react-i18next";
import moment from "moment";
import constants, {
  getNotificationNameById,
  getSchedulerStatusById,
  notifyAttendeesOptions,
  notifyAttendeesOptionsForAllDayEvent,
} from "../../../../constants";
import { getEventIcsLink } from "repositories/scheduler-repository";
import toast from "react-hot-toast";
import CustomModal from "components/CustomModal";
import AddToCalendar from "accountOwner/pages/components/AddToCalendar";
import Loader from "components/Loader";
import { addToCalenderText } from "utils";
import OfficesNamesCommon from "./officesNames";
import { displayCustomMessage } from "accountOwner/pages/AddEvent/addEvents.functions";
import { addEditEventsConst } from "accountOwner/pages/AddEvent/addEvents.constants";
import EmptyPlaceHolderString from "./EmptyPlaceHolderString";
import calendarCross from "assets/images/Calendar-Cross.svg";
import CloseShiftModal from "./CloseShiftModal";
import { useAccountOwnerCommon } from "lib/Context/AccountOwner";

function DetailsTab({ t, eventDetails, seteventDetails }) {
  const { profile } = useAccountOwnerCommon();
  const [toolTipModal, setToolTipModal] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [closeShiftModal, setCloseShiftModal] = useState(false);
  const handleAddToCalendar = async () => {
    setIsDownloading(true);
    try {
      const url = await getEventIcsLink(eventDetails?.id);
      window?.open(url, "_self");
      toast.success(t("fileDownloaded"));
    } catch (error) {
      toast.error(error?.message);
    }
    setIsDownloading(false);
  };

  const selectedEmployeesCount = () => {
    const filteredEmployees =
      eventDetails?.eventEmployees?.filter(
        (employee) => employee.isDeletedForThisSchedulerEvent === false
      ) || [];

    // Implement the display logic
    if (filteredEmployees.length === 0) {
      return t("accountOwner.noEmployeesSelected");
    } else if (filteredEmployees.length === 1) {
      const employee = filteredEmployees[0];
      return employee?.user?.firstName + " " + employee?.user?.lastName;
    } else {
      return filteredEmployees.length + " " + t("Selected");
    }
  };
  const eventCheck = useMemo(
    () => !eventDetails?.isShift,
    [eventDetails?.isShift]
  );
  const eventKey = useMemo(
    () => ({
      eventOrShift: t(
        `accountOwner.${eventDetails?.isShift ? "shift" : "event"}Tab`
      ),
    }),
    [eventDetails?.isShift]
  );
  const repeatMessagesDisplay = useMemo(() => {
    if (!eventDetails.customRepeatPattern) return "";
    return displayCustomMessage(eventDetails);
  }, [eventDetails?.customRepeatPattern]);

  const canCloseShiftToNewRequests =
    eventDetails?.isShift &&
    !eventDetails?.isDraft &&
    Boolean(eventDetails?.publishAsEvent) &&
    !eventDetails?.isShiftClosed;

  return (
    <Row>
      {isDownloading && <Loader />}
      {eventDetails?.isShiftClosed && (
        <Text
          size="14px"
          className="w-100 mb-3"
          marginBottom="5px"
          weight="400"
          color="#EE4F4F"
          backgroundColor="#FDEDED"
          padding="10px"
        >
          {t("scheduler.shiftDetailCloseText")}
        </Text>
      )}
      <Col lg="12">
        <div className="d-flex justify-content-md-end mb-3">
          <div
            className={
              styles["status-tag"] +
              " " +
              (eventDetails?.isDraft
                ? styles["draft-warning-bg"]
                : styles["dark-grey-bg"])
            }
          >
            {eventDetails?.isDraft
              ? t("scheduler.isDraft")
              : t("scheduler.publishedEvent")}
          </div>
        </div>
      </Col>
      <Col lg="6">
        <ul className={styles["white-col-list"]}>
          {eventDetails?.isShift && (
            <li>
              <Text size="12px" marginBottom="5px" weight="400" color="#6f7788">
                {t("scheduler.createdBy")}
              </Text>
              <Text size="14px" marginBottom="0" weight="600" color="#102c42">
                {eventDetails?.createdBy?.fullName}{" "}
                {profile?.id !== eventDetails?.createdById
                  ? ""
                  : `(${t("accountPreferences.you")})`}
              </Text>
            </li>
          )}
          <li>
            <Text size="12px" marginBottom="5px" weight="400" color="#6f7788">
              {t("accountOwner.date")}
            </Text>
            <Text size="14px" marginBottom="0" weight="600" color="#102c42">
              {eventDetails?.date &&
                moment(eventDetails?.date, "YYYY-MM-DDTHH:mm:ss").format(
                  "MMM DD, YYYY - ddd"
                )}
              {eventDetails?.isAllDayEvent
                ? ", " + t("scheduler.agenda.allDayEvent")
                : ""}
              {eventDetails?.office?.timezoneCode &&
                ` (${eventDetails?.office?.timezoneCode})`}
            </Text>
          </li>
          <li>
            <Row>
              <Col xs="6">
                <Text
                  size="12px"
                  marginBottom="5px"
                  weight="400"
                  color="#6f7788"
                >
                  {t("staff.startTime")}
                </Text>
                <Text size="14px" marginBottom="0" weight="600" color="#102c42">
                  {eventDetails?.startTime
                    ? moment(
                        eventDetails?.startTime,
                        "YYYY-MM-DDTHH:mm:ss"
                      ).format("h:mm A")
                    : ""}
                </Text>
              </Col>
              <Col xs="6">
                <Text
                  size="12px"
                  marginBottom="5px"
                  weight="400"
                  color="#6f7788"
                >
                  {t("staff.endTime")}
                </Text>
                <Text size="14px" marginBottom="0" weight="600" color="#102c42">
                  {eventDetails?.endTime
                    ? moment(
                        eventDetails?.endTime,
                        "YYYY-MM-DDTHH:mm:ss"
                      ).format("h:mm A")
                    : ""}
                </Text>
              </Col>
            </Row>
          </li>

          <li>
            <Row>
              <Col sm="6">
                <Text
                  size="12px"
                  marginBottom="5px"
                  weight="400"
                  color="#6f7788"
                >
                  {t("repeat")}
                </Text>
                <Text size="14px" marginBottom="0" weight="600" color="#102c42">
                  {
                    addEditEventsConst
                      .repeatedOptions(t)
                      .find((res) => eventDetails?.repeatedType === res.id)
                      ?.name
                  }
                </Text>
                {eventDetails?.repeatedType && repeatMessagesDisplay && (
                  <Text
                    size="11px"
                    marginBottom="0"
                    weight="400"
                    color="#79869A"
                  >
                    {repeatMessagesDisplay}
                  </Text>
                )}
              </Col>
              {eventDetails?.repeatedType !== 1 &&
                eventDetails?.repeatedEndDate && (
                  <Col sm="6">
                    <Text
                      size="12px"
                      marginBottom="5px"
                      weight="400"
                      color="#6f7788"
                    >
                      {t("accountOwner.endDateRepeatedEvents", {
                        eventOrShift: t(
                          `accountOwner.${
                            eventDetails?.isShift ? "shift" : "event"
                          }s`
                        ),
                      })}
                    </Text>
                    <Text
                      size="14px"
                      marginBottom="0"
                      weight="600"
                      color="#102c42"
                    >
                      {eventDetails?.repeatedEndDate &&
                        moment(
                          eventDetails?.repeatedEndDate,
                          "YYYY-MM-DDTHH:mm:ss"
                        ).format("MMM DD, YYYY - ddd")}{" "}
                      {eventDetails?.office?.timezoneCode &&
                        `(${eventDetails?.office?.timezoneCode})`}
                    </Text>
                  </Col>
                )}
            </Row>
          </li>
          <li>
            <OfficesNamesCommon
              eventDetails={eventDetails}
              titleName={t("accountOwner.offices")}
            />
          </li>
          {eventCheck && (
            <li>
              <Text size="12px" marginBottom="5px" weight="400" color="#6f7788">
                {t("location")}
              </Text>
              <Text
                size="14px"
                marginBottom="0"
                weight="600"
                color="#102c42"
                break
              >
                {eventDetails?.location || <EmptyPlaceHolderString />}
              </Text>
            </li>
          )}
          <li>
            <Text size="12px" marginBottom="5px" weight="400" color="#6f7788">
              {t("superAdmin.role")}
            </Text>
            {eventDetails?.eventRoles?.length > 0 ? (
              eventDetails?.eventRoles?.map((v, index) => (
                <Text
                  size="14px"
                  marginBottom="5px"
                  weight="600"
                  color="#102c42"
                  key={index}
                >
                  {v?.designations?.name}
                </Text>
              ))
            ) : (
              <EmptyPlaceHolderString />
            )}
          </li>
          <li>
            <Text size="12px" marginBottom="5px" weight="400" color="#6f7788">
              {t("accountOwner.employe")}
            </Text>
            <Text size="14px" marginBottom="0" weight="600" color="#102c42">
              {selectedEmployeesCount()}
            </Text>
          </li>
          {eventCheck && (
            <>
              <li>
                <Text
                  size="12px"
                  marginBottom="5px"
                  weight="400"
                  color="#6f7788"
                >
                  {t("accountOwner.notes")}
                </Text>
                <Text size="14px" marginBottom="0" weight="600" color="#102c42">
                  {eventDetails?.note ? (
                    eventDetails?.note
                  ) : (
                    <EmptyPlaceHolderString />
                  )}
                </Text>
              </li>
              <li>
                <Text
                  size="12px"
                  marginBottom="5px"
                  weight="400"
                  color="#6f7788"
                >
                  {t("accountOwner.tags")}
                </Text>
                <div className={styles["tag-list"]}>
                  {eventDetails?.eventTags?.length > 0 ? (
                    eventDetails?.eventTags?.map((v, index) => (
                      <span key={index}>{v?.title}</span>
                    ))
                  ) : (
                    <EmptyPlaceHolderString />
                  )}
                </div>
              </li>
            </>
          )}
          <li>
            <Text size="12px" marginBottom="5px" weight="400" color="#6f7788">
              {t("scheduler.notifyAttendees", eventKey)}
            </Text>
            <Text size="14px" marginBottom="0" weight="600" color="#102c42">
              {getNotificationNameById(
                eventDetails?.reminderBeforeInMins,
                eventDetails?.isAllDayEvent
                  ? notifyAttendeesOptionsForAllDayEvent(eventDetails?.isShift)
                  : notifyAttendeesOptions
              )}
            </Text>
          </li>

          <li>
            <AddToCalendar
              firstIcon={require("assets/images/download-icon.svg").default}
              middleText={t("accountOwner.addToCalendar")}
              secondIcon={require("assets/images/alert-circle.svg").default}
              handleAddToCalendar={handleAddToCalendar}
              setToolTipModal={setToolTipModal}
            />
          </li>
          {canCloseShiftToNewRequests && (
            <li
              style={{
                borderTop: "1px solid #F6F6F6",
              }}
            >
              <button
                className="d-flex align-items-center mt-3 bg-transparent border-0"
                style={{ gap: "4px" }}
                onClick={() => setCloseShiftModal(true)}
              >
                <img src={calendarCross} alt="cross-icon" id="cross-icon" />
                <Text size="12px" weight="500" color="#EE4F4F">
                  {t("scheduler.closeShiftNewReason")}
                </Text>
              </button>
            </li>
          )}
        </ul>
      </Col>

      <Col lg="6">
        <ul className={styles["white-col-list"]}>
          <div className={styles["invitation-status"]}>
            <Text size="20px" marginBottom="10px" weight="500" color="#111b45">
              {t("accountOwner.employeesInvitationStatus")}
            </Text>
            <ul className={styles["invitation-list"]}>
              {eventDetails?.eventEmployees &&
              eventDetails.eventEmployees.length > 0 &&
              eventDetails.eventEmployees.some(
                (item) => !item.isDeletedForThisSchedulerEvent
              ) ? (
                eventDetails.eventEmployees.map((item, index) =>
                  !item.isDeletedForThisSchedulerEvent ? (
                    <li key={index}>
                      {item?.excludeType !== 0 && (
                        <div className="col-12">
                          <Text
                            size="9px"
                            marginBottom="0px"
                            weight="500"
                            width="fit-content"
                            padding="3px 8px"
                            color="#FFFFFF"
                            backgroundColor="#EE4F4F"
                            wordBreak="break-word"
                            borderRadius="4px"
                          >
                            {t("scheduler.excluded")}
                          </Text>
                        </div>
                      )}
                      <div className="col-sm-4 pl-2 pr-2">
                        <Text
                          size="14px"
                          marginBottom="0px"
                          weight="500"
                          color="#587e85"
                          className="pl-1"
                          wordBreak="break-word"
                        >
                          {item.user?.firstName}
                        </Text>
                        <Text
                          size="12px"
                          marginBottom="0px"
                          weight="400"
                          color="#87928D"
                          className="pl-1"
                        >
                          {item?.office?.name}
                        </Text>
                      </div>
                      <Text
                        size="14px"
                        marginBottom="0px"
                        weight="400"
                        color="#535b5f"
                        className="col-sm-4"
                      >
                        {item?.user?.role?.systemRole === 2
                          ? "-"
                          : item?.user?.designation?.name
                          ? item?.user?.designation?.name
                          : "--"}
                      </Text>
                      <Text
                        size="14px"
                        marginBottom="0px"
                        weight="400"
                        color="#535b5f"
                        className={"col-sm-4 " + styles["pending_li"]}
                      >
                        {item?.excludeType
                          ? constants.leaveHoldiday(t)[item?.excludeType] ||
                            null
                          : t(getSchedulerStatusById(item?.status))}
                      </Text>

                      {(item?.reasonForRejection || item?.reasonForCancel) && (
                        <Text
                          size="14px"
                          marginBottom="0"
                          weight="200"
                          color="#587e85"
                          margin="5px 0px"
                          className={styles["para_p"]}
                        >
                          <b>
                            {" "}
                            {item?.reasonForCancel
                              ? t("accountOwner.reasonOfCancellation")
                              : t("accountOwner.reasonOfRejection")}
                            :
                          </b>
                          &nbsp;&nbsp;&nbsp;
                          {item?.reasonForRejection || item?.reasonForCancel}
                        </Text>
                      )}
                    </li>
                  ) : null
                )
              ) : (
                <li style={{ marginLeft: "1px" }}>
                  <span>{t("accountOwner.noUsersAdded")}</span>
                </li>
              )}
            </ul>
          </div>
        </ul>
      </Col>
      {toolTipModal && (
        <CustomModal
          isOpen={toolTipModal}
          setIsOpen={setToolTipModal}
          title={t("accountOwner.addToCalendar")}
          subTitle1={addToCalenderText(eventKey.eventOrShift.toLowerCase())}
          calender={true}
        />
      )}
      {closeShiftModal && (
        <CloseShiftModal
          closeModal={() => setCloseShiftModal(false)}
          eventDetails={eventDetails}
          isModalOpen={closeShiftModal}
          seteventDetails={seteventDetails}
        />
      )}
    </Row>
  );
}

export default withTranslation()(DetailsTab);
